import { formatPersonName } from '@/utils/personName';
/**
 * CartDetail — main record-page for a RAO cart.
 *
 * Layout (per mockups §RAO):
 *   - breadcrumb row with entity chip + meta
 *   - step body + right-hand cart sidebar (top stage ribbon + left stage rail
 *     removed in Bill's v0.70.0 walk; a frozen minimal header replaces them)
 *   - sticky footer "Return" / "draft saved" / "Continue →"
 *
 * Data: ?cartId= resolved via useCart; if the cart isn't loaded yet we show a
 * wait-state overlay; if the cart is archived we render the steps in read-only.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router';
import type { CartDto } from '@retail-ops/shared-types';
import {
  Banner,
  Button,
  EntityIcon,
  Pill,
  WaitStateOverlay,
} from '@/components/primitives';
import { EmptyState } from '@/components/common/EmptyState';
import { useApi } from './hooks/useApi';
import { useSession } from '@/contexts/SessionContext';
import { identifyCart } from '@/utils/cartLabel';
import { useCart as useCartRecord } from './hooks/useCart';
import { useCart as useCartContext } from '@/contexts/CartContext';
import { CartSidebar } from './components/cart/CartSidebar';
import { OnlineAppPdfPreview } from './components/OnlineAppPdfPreview';
import { RAO_STAGES, type RaoStageId } from './constants';
import { PeopleStep } from './steps/PeopleStep';
import { ProductsStep } from './steps/ProductsStep';
import { ReviewStep } from './steps/ReviewStep';
import { SignStep } from './steps/SignStep';
import { CommitFlow } from './CommitFlow';
import { MaintenanceCartView } from './maintenance/MaintenanceCartView';
import { formatRelative } from './utils/formatting';
import { parsePeople } from './utils/cartStateParse';

function readOnlyFor(cart: CartDto): boolean {
  return cart.status === 'committed' || cart.status === 'archived';
}

export function CartDetail(): JSX.Element {
  const { cartId } = useParams<{ cartId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { cart, loading, error, reload } = useCartRecord(cartId);

  // Mirror the loaded cart into CartContext so CartSidebar + cross-module
  // consumers always see the same active cart. We do NOT re-fetch through
  // the provider — useCartRecord already owns the fetch for this route.
  //
  // Root-cause fix (live 2026-06-04 pilot blocker): this effect used to
  // hydrate the context ONLY when the cartId changed. Every step save
  // (PeopleStep / ProductsStep PATCH → onCartUpdated → reload) refreshed
  // the record copy here, while the context copy — the one CartSidebar's
  // cart-inventory rail and its Review CTA read — stayed frozen at the
  // mount-time snapshot (0 people / 0 products on a fresh cart). The rail
  // rendered empty no matter what the FSR added, and Review never enabled.
  // adopt() pushes every fresh record fetch into the context (stale-guarded
  // by cart.version; no extra GET; never touches the dirty patch buffer).
  const cartCtx = useCartContext();
  useEffect(() => {
    if (cart) cartCtx.adopt(cart);
    // cartCtx identity changes whenever its cart updates; the record `cart`
    // is the trigger — adopt() self-guards against echoes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart]);

  const [active, setActive] = useState<RaoStageId>('people');
  // "+ Add joint owner" with nobody eligible in the cart: open People's + New Person (2026-10-16).
  const [addPersonRequested, setAddPersonRequested] = useState<boolean>(false);
  const activeIndex = useMemo(() => RAO_STAGES.findIndex((s) => s.id === active), [active]);
  // Bill 2026-10-14: a cart past approval (signing, opening, opened, or stopped) reopens on the
  // final stage, where its signing and account-opening status live - not on People. Once per cart,
  // on its first load, so the officer can still step back through the earlier stages.
  const landedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!cart || landedForRef.current === cart.cartId) return;
    landedForRef.current = cart.cartId;
    if (cart.status !== 'draft' && cart.status !== 'active') {
      setActive(RAO_STAGES[RAO_STAGES.length - 1]!.id);
    }
  }, [cart]);

  // Online-imported carts carry the source JotForm application PDF as a
  // regulated artifact; offer a side-panel preview while the FSR curates.
  const isOnlineImport = cart?.origin === 'online';
  const [pdfOpen, setPdfOpen] = useState<boolean>(false);

  // ── Regenerate-disclosures (Bill 2026-07-01) ─────────────────────────────
  // At awaiting-signatures the rendered documents + pipeline snapshot were
  // built from the cart's content, so content edits are LOCKED (server
  // enforces CART_CONTENT_LOCKED too). The escape hatch is Regenerate: cancel
  // the signing session, drop back to Review, edit, re-submit → fresh package.
  const { client } = useApi();
  const { setActiveCart } = useSession();

  // Bill 2026-09-11: "Why do we even save an empty cart?" We don't. A cart
  // with no person and no product is discarded when the FSR leaves it - the
  // footer says Discard instead of Save & close, and any other way out
  // (rail, Home, browser back) discards on unmount. The server purges the row
  // tree (Cart_Archive -> CartPurge) and the Resume pin is dropped so nothing
  // points at it afterwards.
  const cartIsEmpty = useMemo(() => {
    if (!cart) return false;
    const id = identifyCart(cart.peopleJson, cart.productsJson);
    return !id.who && !id.what;
  }, [cart]);
  const emptyRef = useRef(false);
  emptyRef.current = cartIsEmpty;
  const discardedRef = useRef(false);
  const mountedAtRef = useRef(Date.now());
  const discardEmpty = useCallback((): void => {
    if (discardedRef.current || !cartId) return;
    discardedRef.current = true;
    setActiveCart(null);
    void client.retail.carts
      .archive(cartId, { reason: 'Cancelled', details: 'Empty cart discarded on exit' })
      .catch(() => { /* the overnight sweep purges it anyway (CHG-0110) */ });
  }, [cartId, client, setActiveCart]);
  const discardRef = useRef(discardEmpty);
  discardRef.current = discardEmpty;
  useEffect(() => () => {
    // Unmount only (empty deps). The 1.5s floor keeps a dev StrictMode
    // double-mount from discarding a cart the FSR just opened.
    if (emptyRef.current && Date.now() - mountedAtRef.current > 1500) discardRef.current();
  }, []);
  const [regenBusy, setRegenBusy] = useState<boolean>(false);
  const [regenConfirm, setRegenConfirm] = useState<string | null>(null);
  const [regenError, setRegenError] = useState<string | null>(null);

  const awaitingSignatures = cart?.status === 'awaiting-signatures';
  // BACKLOG-1673 — under the never-wait commit policy the cart moves to 'committed' while
  // the in-branch ceremony is still running. Keep SignStep mounted until it says it is done.
  const [ceremonyActive, setCeremonyActive] = useState<boolean>(false);
  // Bill 2026-10-02 — signers left outstanding after the opening finished: the
  // completion panel's "Sign in branch" (or a follow-up task elsewhere, via
  // navigation state) re-mounts SignStep on the committed cart.
  const [resumeSigning, setResumeSigning] = useState<boolean>(
    () => Boolean((location.state as { resumeSigning?: boolean } | null)?.resumeSigning),
  );

  // Plain-language page title: who the application is FOR (never a backend id).
  const applicationTitle = useMemo(() => {
    if (!cart) return 'New application';
    const people = parsePeople(cart);
    const primary = people.find((p) => p.role === 'primary') ?? people[0];
    const name = formatPersonName(primary) || primary?.fullName || '';
    return name ? `Application — ${name}` : 'New application';
  }, [cart]);

  // ── Manual Aurora bridge (Bill 2026-07-02) ───────────────────────────────
  // Until automatic Aurora delivery exists, the FSR downloads the signed
  // package + manifest as one zip and files it per current procedure.
  const [auroraBusy, setAuroraBusy] = useState<boolean>(false);
  const [auroraError, setAuroraError] = useState<string | null>(null);
  const canAurora = cart?.status === 'committed' || cart?.status === 'archived';

  const handleAuroraDownload = async (): Promise<void> => {
    if (!cart || auroraBusy) return;
    setAuroraBusy(true);
    setAuroraError(null);
    try {
      const blob = await client.retail.carts.auroraPackage(cart.cartId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `aurora-${cart.cartId.slice(0, 8)}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (err) {
      setAuroraError(
        err instanceof Error ? err.message : 'Could not build the Aurora package. Try again.',
      );
    } finally {
      setAuroraBusy(false);
    }
  };

  // ── Customer copy (BACKLOG-1298) — reprint from the application page. The
  // server refuses until signing is finished, so it is offered once the
  // application is past signing, including one that stopped while opening accounts.
  const [copyBusy, setCopyBusy] = useState<boolean>(false);
  const [copyError, setCopyError] = useState<string | null>(null);
  const canCustomerCopy = canAurora || cart?.status === 'committing' || cart?.status === 'pipeline-error';

  const handleCustomerCopy = async (): Promise<void> => {
    if (!cart || copyBusy) return;
    setCopyBusy(true);
    setCopyError(null);
    try {
      const blob = await client.retail.carts.customerCopy(cart.cartId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `customer-copy-${cart.cartId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (err) {
      setCopyError(err instanceof Error ? err.message : "Couldn't make the customer copy. Try again.");
    } finally {
      setCopyBusy(false);
    }
  };

  const handleRegenerate = async (confirmVoid: boolean): Promise<void> => {
    if (!cart || regenBusy) return;
    setRegenBusy(true);
    setRegenError(null);
    try {
      const r = await client.retail.signing.internal.regenerate(
        cart.cartId,
        confirmVoid ? { confirmVoidSignatures: true } : {},
      );
      if (r.success) {
        setRegenConfirm(null);
        setActive('review');
        reload();
      } else {
        setRegenError(r.error ?? 'Could not regenerate the documents. Try again.');
      }
    } catch (err) {
      const e = err as { code?: string; message?: string };
      if (e.code === 'SIGNATURES_ALREADY_CAPTURED') {
        setRegenConfirm(
          e.message ??
            'Signatures were already captured for this application. Regenerating voids them — every signer signs the new documents again.',
        );
      } else {
        setRegenError(e.message ?? 'Could not regenerate the documents. Try again.');
      }
    } finally {
      setRegenBusy(false);
    }
  };

  if (loading) {
    return <WaitStateOverlay headline="Loading cart…" status={cartId ? `Cart ${cartId}` : undefined} />;
  }
  if (error) {
    return (
      <EmptyState
        title="Unable to load cart"
        message={error}
        action={<Button variant="primary" onClick={reload}>Retry</Button>}
      />
    );
  }
  if (!cart) {
    return (
      <EmptyState
        title="Cart not found"
        message={`No cart exists for id ${cartId ?? '—'}.`}
        action={<Button variant="ghost" onClick={() => navigate('/rao')}>← Back to RAO</Button>}
      />
    );
  }

  const ro = readOnlyFor(cart);

  // Per-stage advance prerequisites. Pre-fix the only gate was "is the cart
  // locked?" — which let an FSR advance the People step with zero applicants
  // (Bill 2026-04-27 demo prep). Each stage now declares its own minimum
  // prerequisite; the sticky-footer [Continue →] button is disabled with the
  // first failing reason surfaced as a tooltip.
  const computeAdvanceDisabled = (peopleJson: string | null | undefined): string | undefined => {
    if (ro) return 'Cart is locked — no further edits.';
    if (active === 'people') {
      let count = 0;
      if (peopleJson) {
        try {
          const raw = JSON.parse(peopleJson) as unknown;
          if (Array.isArray(raw)) count = raw.length;
          else if (raw && typeof raw === 'object'
            && Array.isArray((raw as { people?: unknown }).people)) {
            count = (raw as { people: unknown[] }).people.length;
          }
        } catch { /* malformed JSON → treat as zero */ }
      }
      if (count === 0) return 'Add at least one applicant before continuing.';
    }
    return undefined;
  };
  const advanceDisabled = computeAdvanceDisabled(cart.peopleJson);

  const onAdvance = (): void => {
    const next = RAO_STAGES[activeIndex + 1];
    if (next) setActive(next.id);
  };

  const onReturn = (): void => {
    const prev = RAO_STAGES[activeIndex - 1];
    if (prev) setActive(prev.id);
  };

  const isFirst = activeIndex === 0;
  const isFinal = activeIndex === RAO_STAGES.length - 1;

  // Account maintenance (Bill 2026-10-17): a maintenance cart has its own page; signing and the commit screens are
  // the opening ones, rendered inside it.
  if (cart.mode === 'Maintenance') return <MaintenanceCartView cart={cart} onCartUpdated={() => reload()} />;

  return (
    <div className="rao-record">
      <div className="rao-record__breadcrumb">
        <EntityIcon type="application" code="AO" title="Account application" />
        <div className="rao-record__app-title">
          {/* 2026-07-02 — the user sees WHO the application is for, never a
              backend id (Cart GUID / origin enum are internal-only). */}
          <span className="rao-record__app-name">{applicationTitle}</span>
          <span className="rao-record__app-meta">
            Officer <strong>{cart.officerCode}</strong> · Branch {cart.branch} · updated {formatRelative(cart.updatedAt)}
          </span>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 'var(--sp-4)', alignItems: 'center' }}>
          {isOnlineImport && (
            <Button
              variant={pdfOpen ? 'primary' : 'ghost'}
              onClick={() => setPdfOpen((v) => !v)}
              title="View the imported online application PDF alongside the cart"
            >
              📄 {pdfOpen ? 'Hide application' : 'View application'}
            </Button>
          )}
          {/* Bill 2026-10-14: an opened cart's completion panel already offers both downloads (with the
              customer-copy task); the header copies only show where that panel doesn't. */}
          {canAurora && cart.status !== 'committed' && (
            <Button
              variant="ghost"
              onClick={() => void handleAuroraDownload()}
              disabled={auroraBusy}
              title={auroraError ?? 'Download the signed document package (zip + manifest) for manual Aurora filing'}
            >
              {auroraBusy ? 'Building package…' : '⬇ Download for Aurora'}
            </Button>
          )}
          {canCustomerCopy && cart.status !== 'committed' && (
            <Button
              variant="ghost"
              onClick={() => void handleCustomerCopy()}
              disabled={copyBusy}
              title={copyError ?? "Download the customer's copy (signed documents and disclosures) to print"}
            >
              {copyBusy ? 'Making copy…' : '⬇ Customer copy'}
            </Button>
          )}
          <Pill variant={cart.status === 'committed' ? 'ok' : cart.status === 'pipeline-error' ? 'err' : cart.status === 'awaiting-signatures' ? 'warn' : 'info'}>
            {/* PCRAWL-08 — plain-language status; the FSR shouldn't see raw enums. */}
            {cart.status === 'committed' ? 'Completed'
              : cart.status === 'pipeline-error' ? 'Needs attention'
              : cart.status === 'awaiting-signatures' ? 'Awaiting signatures'
              : cart.status === 'committing' ? 'Opening accounts'
              : cart.status === 'archived' ? 'Archived'
              : cart.status === 'active' ? 'In progress'
              : 'Draft'}
          </Pill>
        </div>
      </div>

      {copyError && (
        <div style={{ margin: '0 0 var(--sp-5) 0' }}>
          <Banner tone="err">{copyError}</Banner>
        </div>
      )}

      {awaitingSignatures && !ro && (
        <div style={{ margin: '0 0 var(--sp-5) 0' }}>
          <Banner tone={regenConfirm ? 'warn' : 'info'}>
            {regenConfirm ? (
              <>
                {regenConfirm}{' '}
                <Button
                  variant="primary"
                  onClick={() => void handleRegenerate(true)}
                  disabled={regenBusy}
                >
                  {regenBusy ? 'Regenerating…' : 'Void signatures & regenerate'}
                </Button>{' '}
                <Button variant="ghost" onClick={() => setRegenConfirm(null)} disabled={regenBusy}>
                  Keep current documents
                </Button>
              </>
            ) : (
              <>
                Documents were generated for signing, so the application is locked while
                signatures are collected. Need to change something — or did the signing
                session expire? <strong>Regenerate documents</strong>: the current signing
                session is cancelled, you edit and re-submit, and a fresh document set is
                created for everyone to sign.{' '}
                <Button
                  variant="ghost"
                  onClick={() => void handleRegenerate(false)}
                  disabled={regenBusy}
                >
                  {regenBusy ? 'Regenerating…' : '↻ Regenerate documents'}
                </Button>
              </>
            )}
            {regenError && (
              <>
                {' '}
                <strong>{regenError}</strong>
              </>
            )}
          </Banner>
        </div>
      )}

      <div className="rao-record__body rao-record__body--with-sidebar">
        <div className="rao-record__main">
          {/* Wave 1 #2.8 — 3-stage rail (Bill 2026-04-28). Ownership matrix +
              3 sub-sections render INLINE on the Products stage; CIP/OFAC
              capture moves to the per-person dialog within People; Sign +
              Commit are pipeline phases that render on Review & Submit
              based on cart.status, not separate stepper pills. */}
          {/* Content is LOCKED at awaiting-signatures (server enforces
              CART_CONTENT_LOCKED) — the regenerate banner above is the way out. */}
          {active === 'people' && (
            <PeopleStep
              cart={cart}
              readOnly={ro || awaitingSignatures}
              onCartUpdated={reload}
              openAddPersonOnMount={addPersonRequested}
              onAddPersonOpened={() => setAddPersonRequested(false)}
            />
          )}
          {active === 'products' && (
            <ProductsStep
              cart={cart}
              readOnly={ro || awaitingSignatures}
              onCartUpdated={reload}
              onAddPerson={() => { setAddPersonRequested(true); setActive('people'); }}
            />
          )}
          {active === 'review' && (
            <>
              {/* BACKLOG-0746 (Bill 2026-06-12) — the zombie ComplianceStep
                  mount that lived here was DELETED. It was retired 2026-05-08
                  (complianceRuleApplicationsEditor.tsx:357, OfacScanSection.tsx:4)
                  but kept mounting a live OFAC pull + CIP scan stub on Review.
                  The PDW owns CIP + OFAC; Review only summarizes (per-person
                  readiness line inside ReviewStep's Applicants section). */}
              {/* S1.1 (2026-06-11) — commit reachability fix. Status-driven
                  surfaces on Review & Submit:
                    · draft/active        → ReviewStep with its action row —
                      "Submit Application →" + PreSubmitConfirmModal is the
                      Round-3-locked commit ceremony (SPEC Screen 27). The
                      modal now runs /signing/start first (S1.2), POSTs
                      /v3/carts/{id}/commit, and ALWAYS raises onCartUpdated
                      so this page re-fetches and the tracker mounts
                      (previously the success callback never fired and the
                      FSR was stranded).
                    · awaiting-signatures → SignStep runs the capture ceremony
                      while CommitFlow (screen 3) keeps polling /commit/status
                      below it — the AwaitingSignaturesBanner + progress bar
                      pick up the queue-side pipeline resume automatically.
                    · committing/committed/pipeline-error → CommitFlow screen 3
                      live tracker, as before.
                    · archived            → plain read-only ReviewStep. */}
              {(cart.status === 'awaiting-signatures' || ((ceremonyActive || resumeSigning) && (cart.status === 'committing' || cart.status === 'committed'))) && (
                <div style={{ marginTop: 'var(--sp-9)' }}>
                  <SignStep cart={cart} onAllSigned={() => { setResumeSigning(false); reload(); }} onCeremonyActiveChange={setCeremonyActive} />
                </div>
              )}
              {(cart.status === 'draft' || cart.status === 'active' || cart.status === 'archived') && (
                <div style={{ marginTop: 'var(--sp-9)' }}>
                  <ReviewStep cart={cart} onCartUpdated={() => reload()} />
                </div>
              )}
              {(cart.status === 'awaiting-signatures'
                || cart.status === 'committing'
                || cart.status === 'committed'
                || cart.status === 'pipeline-error') && (
                <div style={{ marginTop: 'var(--sp-9)' }}>
                  <CommitFlow
                    cart={cart}
                    onCartUpdated={reload}
                    onExit={() => navigate('/rao')}
                    onResumeSigning={() => setResumeSigning(true)}
                  />
                </div>
              )}
            </>
          )}
        </div>

        {/* Bill v0.70.0 walk — the sidebar "Review" CTA competed with the
            footer "Continue →"; navigation is Return / Continue only now. */}
        <CartSidebar />
      </div>

      <footer className="rao-record__footer" data-tour="cart-footer">
        <Button
          variant="ghost"
          onClick={onReturn}
          disabled={isFirst}
          title={isFirst ? 'Already on the first stage' : undefined}
        >
          ← Back
        </Button>
        <span className="rao-record__footer-status">
          Saved {formatRelative(cart.updatedAt)}
          {/* CHG-0146 (Reid's review 10/30, Connor 11/2): a real button, and it goes to the Home screen. */}
          <Button
            variant="ghost"
            onClick={() => {
              if (cartIsEmpty) discardEmpty();
              navigate('/');
            }}
            title={cartIsEmpty ? 'Nothing has been added - this cart is discarded, not saved' : undefined}
          >
            {cartIsEmpty ? 'Discard' : 'Save & close'}
          </Button>
        </span>
        {/* 2026-07-02 — on the final (Review) stage the footer renders NO
            primary button at all. The old permanently-disabled "Submit above ↑"
            competed with ReviewStep's real "Proceed to Signing" action and read
            as a broken control. */}
        {/* Final stage: ReviewStep portals Decline / Approve Cart in here so they stay on screen. */}
        {isFinal && <div id="rao-footer-actions" className="rao-record__footer-actions" />}
        {!isFinal && (
          <Button
            variant="primary"
            onClick={onAdvance}
            disabled={ro || !!advanceDisabled}
            title={advanceDisabled}
          >
            Continue →
          </Button>
        )}
      </footer>

      {isOnlineImport && pdfOpen && (
        <OnlineAppPdfPreview cartId={cart.cartId} onClose={() => setPdfOpen(false)} />
      )}
    </div>
  );
}
